import { useId, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { CalendarIcon, CaretDownIcon } from '@phosphor-icons/react'
import { Calendar, DateObject } from 'react-multi-date-picker'
import { DATE_PICKER_SETTINGS } from '../../../constants/constant'
import { formatDateRangeSummary, pickerValueToDateRange } from '../helpers/routeExecutionFilters'

export default function RouteExecutionDateRangeCard({ title, range, minDate, maxDate, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const calendarId = useId()
  const hasRange = Boolean(range.start || range.end)
  // Keep both boundaries for a completed single-day range. Collapsing them
  // makes the calendar interpret the next click as an unfinished selection.
  const calendarValue = useMemo(
    () => [range.start, range.end].filter(Boolean).map((date) => new DateObject(date.toDate())),
    [range.start, range.end]
  )

  const handleCalendarChange = (dates) => {
    onChange(pickerValueToDateRange(dates))
    if (Array.isArray(dates) && dates.length >= 2) setIsOpen(false)
  }

  return (
    <Box className="route-date-range-card">
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography component="h3" sx={{ m: 0, fontSize: 10, fontWeight: 700, color: '#273e54' }}>
          {title}
        </Typography>
        {hasRange && (
          <Button size="small" onClick={() => onChange({ start: null, end: null })}
            sx={{ minWidth: 'auto', borderRadius: 2, textTransform: 'none', color: '#597fa9' }}>
            Limpiar
          </Button>
        )}
      </Stack>
      <button type="button" className="route-date-range-trigger" onClick={() => setIsOpen((open) => !open)}
        aria-label={`${title}: ${formatDateRangeSummary(range, 'Seleccionar rango')}`}
        aria-expanded={isOpen} aria-controls={calendarId}>
        <span className="route-date-range-icon" aria-hidden="true"><CalendarIcon size={21} /></span>
        <span className="route-date-range-copy">
          <strong>{formatDateRangeSummary(range, 'Seleccionar rango')}</strong>
          <span>{isOpen ? 'Elige un día o un rango de fechas' : 'Abre el calendario para elegir las fechas'}</span>
        </span>
        <CaretDownIcon size={16} aria-hidden="true" style={{ transform: isOpen ? 'rotate(180deg)' : undefined }} />
      </button>
      {isOpen && (
        <Box id={calendarId} className="route-date-range-calendar" role="group" aria-label={`Calendario: ${title}`}>
          <Calendar
            className="report-period-calendar"
            range
            rangeHover
            numberOfMonths={1}
            months={DATE_PICKER_SETTINGS.months}
            weekDays={DATE_PICKER_SETTINGS.weekDays}
            weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
            highlightToday={false}
            minDate={minDate?.toDate()}
            maxDate={maxDate?.toDate()}
            currentDate={new DateObject((range.start || minDate || maxDate).toDate())}
            value={calendarValue}
            onChange={handleCalendarChange}
          />
          <Typography sx={{ mt: 1, fontSize: 7, color: '#7890a5', textAlign: 'center' }}>
            Para un solo día, selecciona dos veces la misma fecha.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
